// Section card + ColorField, moved out of PublisherForm.tsx so the
// publisher-facing intake can compose the same chrome.

import React from 'react';
import { Check } from 'lucide-react';
import { labelClass } from './publisherFieldStyles';

interface SectionProps {
  title: string;
  /** Plain text or rich content (the intake bolds key constraints). */
  subtitle?: React.ReactNode;
  icon: React.FC<{ className?: string }>;
  // Rendered to the right of the title. The admin onboarding page uses this to
  // mark which sections came from a publisher's submission and which are
  // internal-only.
  badge?: React.ReactNode;
  /**
   * Live completion state, used by the publisher intake so someone filling the
   * form can see which sections are done. Undefined (the admin form) renders
   * no indicator at all; true turns the card's edge and icon green with a
   * check chip.
   */
  complete?: boolean;
  /**
   * Stop clipping content to the card. The card normally hides overflow so the
   * header rule meets the rounded corners cleanly, but that also clips any
   * absolutely-positioned child — notably the DMA search dropdown, which is
   * `absolute z-10` and gets cut off at the card's edge.
   */
  allowOverflow?: boolean;
  children: React.ReactNode;
}

export function Section({
  title, subtitle, icon: Icon, badge, complete, allowOverflow = false, children,
}: SectionProps) {
  const done = complete === true;
  return (
    <section
      className={
        'rounded-2xl border bg-white transition-colors '
        + (done ? 'border-emerald-300 ' : 'border-slate-200 ')
        + (allowOverflow ? '' : 'overflow-hidden')
      }
      style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.08), 0 18px 40px -16px rgba(15,23,42,0.28)' }}
    >
      <header
        className={
          'flex items-start gap-3 px-5 sm:px-6 py-4 border-b '
          + (done ? 'border-emerald-200/70' : 'border-slate-200/70')
        }
      >
        <div
          className={
            'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors '
            + (done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700')
          }
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">{title}</h3>
            {badge}
            {done && (
              <span
                data-testid={`section-complete-${title}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800"
              >
                <Check className="w-3 h-3" />
                Done
              </span>
            )}
          </div>
          {subtitle && <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>}
        </div>
      </header>
      <div className="px-5 sm:px-6 py-5">{children}</div>
    </section>
  );
}

export const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white pl-1.5 pr-3 py-1.5 focus-within:border-slate-900 transition-colors">
      <input
        type="color"
        value={value || '#000000'}
        // CTV-745: browsers fire no change event when the picked color equals
        // the input's current value, so an UNSET color (rendered as the black
        // fallback) could never be set to pure black (seen in Safari). Seed
        // the shown fallback as the real value when the picker is opened;
        // any other pick still fires change, and the hex field clears back
        // to unset.
        onFocus={() => {
          if (!value) onChange('#000000');
        }}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 rounded-md border-0 cursor-pointer bg-transparent p-0"
        aria-label={`${label} color picker`}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="flex-1 min-w-0 bg-transparent border-0 px-1 py-0.5 text-[13px] font-mono tabular-nums text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-0"
      />
    </div>
  </div>
);
